import { Injectable, OnDestroy, inject } from "@angular/core";
import { Subscription, from, pairwise, startWith } from "rxjs";
import { concatMap } from "rxjs/operators";
import { PLATFORM_ADAPTER, PlatformAdapter } from "../core/platform/platform.adapter";
import { ClientTokenService } from "../services/auth/client-token.service";
import { WebSessionBootstrapService } from "../services/auth/web-session-bootstrap.service";
import { SyncWsClientService } from "./sync-ws-client.service";

/**
 * Web-only glue: keeps SyncWsClientService connected whenever there's a
 * valid clientToken. Inject eagerly (AppComponent) so the listener is
 * wired before any token state change.
 *
 * **Serialized against the session bootstrap.** Before connecting the WS,
 * we await `WebSessionBootstrapService.readyForToken(next)` so REMOTE
 * frames don't fire TableChangeNotifier re-queries against a classCom that
 * hasn't been populated yet for the new identity. Without this, a rapid
 * A→null→B token sequence could open a WS for B while WebSession is still
 * mid-resetIdentity for A — the cross-identity re-query would read against
 * the wrong activeDutyId/registrationId until B's bootstrap completes.
 */
@Injectable({ providedIn: "root" })
export class WebSyncBootstrapService implements OnDestroy {
  private platform = inject<PlatformAdapter>(PLATFORM_ADAPTER);
  private clientToken = inject(ClientTokenService);
  private webSessionBootstrap = inject(WebSessionBootstrapService);
  private syncWs = inject(SyncWsClientService);

  private sub: Subscription | null = null;

  constructor() {
    if (this.platform.isNative) return;
    // pairwise+startWith(null) means we only react to actual transitions —
    // no gratuitous disconnect() on the BehaviorSubject's initial null.
    // concatMap serializes the async readyForToken wait so two rapid
    // transitions can't open/close the WS out of order.
    this.sub = this.clientToken.tokenObs
      .pipe(
        startWith(null),
        pairwise(),
        concatMap(([prev, next]) =>
          from(
            (async () => {
              if (next) {
                // Wait for the session bootstrap to settle. We don't care
                // about the BootstrapResult — even 'error'/'empty' means
                // classCom is in a defined state. 'unauthorized' means
                // bootstrap routed to /login and tokenObs will emit null
                // next; we still open here and disconnect on that emission.
                const result = await this.webSessionBootstrap.readyForToken(next);
                // Sanity check: if the token rotated mid-wait, skip; the
                // next emission will open the WS for the new token.
                if (this.clientToken.getTokenSync() !== next) return;
                if (result.status === "unauthorized") return;
                this.syncWs.connect(next);
              } else if (prev) {
                this.syncWs.disconnect();
              }
            })()
          )
        )
      )
      .subscribe({
        error: (err) => {
          console.error("[webSync] subscription died:", err);
        },
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
